import type { FxConversionMetadata, FxRateLog } from '../types/fx'

import { buildConvertedFxConversionMetadata, buildUnavailableFxConversionMetadata } from './fxConversionContracts'
import type { LotCostPerKgRow } from './lotCostPerKg'

export type LotCostPerKgFxRow = LotCostPerKgRow & {
  convertedCurrency: string | null
  convertedTotalCostsCents: number | null
  convertedTotalCostsMajor: string | null
  convertedCostPerKgMajor: string | null
  fxConversionMetadata: FxConversionMetadata | null
}

type FindRateArgs = {
  baseCurrency: string
  quoteCurrency: string
  asOf: string
}

type ApplyFxConversionArgs = {
  targetCurrency: string
  asOf: string
  findRate: (args: FindRateArgs) => Promise<FxRateLog | undefined>
}

const IDENTITY_ROUNDING_POLICY_VERSION = 'identity-v1'
const IDENTITY_RATE_VALUE = '1.0000000000'
const BIGINT_ZERO = BigInt(0)
const BIGINT_ONE = BigInt(1)
const BIGINT_TWO = BigInt(2)
const BIGINT_TEN = BigInt(10)

const toUnavailableRow = (
  row: LotCostPerKgRow,
  targetCurrency: string,
  asOf: string,
  detail: string
): LotCostPerKgFxRow => ({
  ...row,
  convertedCurrency: targetCurrency,
  convertedTotalCostsCents: null,
  convertedTotalCostsMajor: null,
  convertedCostPerKgMajor: null,
  fxConversionMetadata: buildUnavailableFxConversionMetadata({
    sourceAmountCents: row.totalCostsCents,
    sourceCurrency: row.currency,
    targetCurrency,
    asOf,
    roundingPolicyVersion: 'unavailable',
    detail
  })
})

const parseDecimalToScaledInt = (value: string): { digits: bigint; scale: bigint } => {
  const trimmed = value.trim()
  if (!/^[-+]?\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error(`Invalid FX rate value: ${value}`)
  }

  const sign = trimmed.startsWith('-') ? -BIGINT_ONE : BIGINT_ONE
  const unsigned = trimmed.replace(/^[-+]/, '')
  const [wholePart, fractionPart = ''] = unsigned.split('.')
  const digits = BigInt(`${wholePart}${fractionPart}` || '0') * sign
  const scale = BIGINT_TEN ** BigInt(fractionPart.length)

  return { digits, scale }
}

const roundHalfUpDiv = (numerator: bigint, denominator: bigint): bigint => {
  const quotient = numerator / denominator
  const remainder = numerator % denominator
  const absRemainder = remainder < BIGINT_ZERO ? -remainder : remainder
  if (absRemainder * BIGINT_TWO < denominator) return quotient
  return numerator >= BIGINT_ZERO ? quotient + BIGINT_ONE : quotient - BIGINT_ONE
}

const convertCentsWithRate = (sourceAmountCents: number, rateValue: string, roundingPolicyVersion: string): number => {
  if (roundingPolicyVersion !== 'v1') {
    throw new Error(`Unsupported FX rounding policy version: ${roundingPolicyVersion}`)
  }

  const { digits, scale } = parseDecimalToScaledInt(rateValue)
  const converted = roundHalfUpDiv(BigInt(sourceAmountCents) * digits, scale)
  return Number(converted)
}

const formatMajorFromCents = (amountCents: number): string => (amountCents / 100).toFixed(2)

const computeConvertedCostPerKg = (convertedTotalCostsCents: number, totalHarvestKg: string): string | null => {
  const totalHarvestKgNum = Number(totalHarvestKg)
  if (!Number.isFinite(totalHarvestKgNum) || totalHarvestKgNum <= 0) return null
  const perKg = (convertedTotalCostsCents / 100) / totalHarvestKgNum
  if (!Number.isFinite(perKg)) return null
  return perKg.toFixed(4)
}

const toIdentityRow = (row: LotCostPerKgRow, targetCurrency: string, asOf: string): LotCostPerKgFxRow => ({
  ...row,
  convertedCurrency: targetCurrency,
  convertedTotalCostsCents: row.totalCostsCents,
  convertedTotalCostsMajor: row.totalCostsMajor,
  convertedCostPerKgMajor: row.costPerKgMajor,
  fxConversionMetadata: buildConvertedFxConversionMetadata({
    sourceAmountCents: row.totalCostsCents,
    sourceCurrency: row.currency,
    targetCurrency,
    convertedAmountCents: row.totalCostsCents,
    asOf,
    roundingPolicyVersion: IDENTITY_ROUNDING_POLICY_VERSION,
    rate: {
      rateSnapshotId: `identity:${row.currency}:${targetCurrency}`,
      rateSourceId: 'identity',
      rateSourceVersion: 'v1',
      rateAsOf: asOf,
      rateValue: IDENTITY_RATE_VALUE
    }
  })
})

export const applyFxConversionToLotCostPerKgRows = async (
  rows: readonly LotCostPerKgRow[],
  args: ApplyFxConversionArgs
): Promise<LotCostPerKgFxRow[]> => {
  const cache = new Map<string, Promise<FxRateLog | undefined>>()

  const getRate = (baseCurrency: string): Promise<FxRateLog | undefined> => {
    const key = `${baseCurrency}::${args.targetCurrency}::${args.asOf}`
    const cached = cache.get(key)
    if (cached) return cached

    const next = args.findRate({
      baseCurrency,
      quoteCurrency: args.targetCurrency,
      asOf: args.asOf
    })
    cache.set(key, next)
    return next
  }

  return Promise.all(rows.map(async (row) => {
    if (!row.currency || row.currency === 'MIXED') {
      return toUnavailableRow(row, args.targetCurrency, args.asOf, 'No single source currency available for conversion')
    }

    if (row.currency === args.targetCurrency) {
      return toIdentityRow(row, args.targetCurrency, args.asOf)
    }

    const rate = await getRate(row.currency)
    if (!rate) {
      return toUnavailableRow(
        row,
        args.targetCurrency,
        args.asOf,
        `No FX snapshot available for ${row.currency}/${args.targetCurrency} at or before asOf`
      )
    }

    const convertedTotalCostsCents = convertCentsWithRate(
      row.totalCostsCents,
      rate.rateValue,
      rate.roundingPolicyVersion
    )

    return {
      ...row,
      convertedCurrency: args.targetCurrency,
      convertedTotalCostsCents,
      convertedTotalCostsMajor: formatMajorFromCents(convertedTotalCostsCents),
      convertedCostPerKgMajor: computeConvertedCostPerKg(convertedTotalCostsCents, row.totalHarvestKg),
      fxConversionMetadata: buildConvertedFxConversionMetadata({
        sourceAmountCents: row.totalCostsCents,
        sourceCurrency: row.currency,
        targetCurrency: args.targetCurrency,
        convertedAmountCents: convertedTotalCostsCents,
        asOf: args.asOf,
        roundingPolicyVersion: rate.roundingPolicyVersion,
        rate: {
          rateSnapshotId: rate.id,
          rateSourceId: rate.sourceId,
          rateSourceVersion: rate.sourceVersion,
          rateAsOf: rate.asOf,
          rateValue: rate.rateValue
        }
      })
    }
  }))
}